import React, { useState } from 'react';
import { Card, Button, Form, Alert, Spinner, Container } from 'react-bootstrap';
import Navbar from "./navbar";
import Contact from "./Contact";
import Footer from "./Footer";
import { createQuery } from '../hooks/useQueries';
import { useUserAuth } from '../Context/UserAuthContext';

const AskLawyer = () => {
  const { user } = useUserAuth();
  const [queryText, setQueryText] = useState('');
  const [lawyerType, setLawyerType] = useState('General');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [currentResponse, setCurrentResponse] = useState(null);
  const [showResponse, setShowResponse] = useState(false);

  const lawyerTypes = [
    'General',
    'Family Lawyer',
    'Criminal Defense Lawyer',
    'Business Lawyer',
    'Estate Planning Lawyer',
    'Civil Rights Lawyer',
    'Tax Lawyer',
    'Immigration Lawyer'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!queryText.trim()) {
      setError('Please enter your legal question');
      return;
    }

    setSubmitting(true);
    setError('');
    setShowResponse(false);
    setCurrentResponse(null);

    console.log("Submitting query...");
    console.log("Query:", queryText);
    console.log("Lawyer Type:", lawyerType);

    const result = await createQuery(
      user.uid,
      user.displayName || user.email,
      queryText,
      lawyerType
    );

    setSubmitting(false);

    if (result.success) {
      // Show the response that was generated
      setCurrentResponse({
        query: queryText,
        lawyerType: lawyerType,
        response: result.response,
        timestamp: new Date()
      });
      setShowResponse(true);
      
      // Clear the form for next query
      setQueryText('');
      setLawyerType('General');
      
      // Scroll to response
      setTimeout(() => {
        document.getElementById('response-section')?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    } else {
      setError(result.error || 'Failed to submit query. Please try again.');
    }
  };

  const handleAskAnother = () => {
    setShowResponse(false);
    setCurrentResponse(null);
    setQueryText('');
    setLawyerType('General');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <main className="main-content">
        <Container className="py-4">
          <div className="section-header">
            <h1>Ask AI Lawyer</h1>
            <p>Get instant legal consultation based on Pakistan Penal Code and Constitution</p>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-8">
              {/* Query Form */}
              <Card className="mb-4">
                <Card.Body>
                  <h5 style={{ color: '#c9a961', marginBottom: '1.5rem' }}>
                    Submit Your Legal Question
                  </h5>

                  {error && (
                    <Alert variant="danger" className="mb-3">
                      {error}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Select Lawyer Type</Form.Label>
                      <Form.Select
                        value={lawyerType}
                        onChange={(e) => setLawyerType(e.target.value)}
                        disabled={submitting}
                      >
                        {lawyerTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Your Legal Question</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        placeholder="Enter your legal question here..."
                        value={queryText}
                        onChange={(e) => setQueryText(e.target.value)}
                        disabled={submitting}
                      />
                      <Form.Text className="text-muted">
                        Ask any question related to Pakistani law. Our AI will provide guidance based on PPC and Constitution.
                      </Form.Text>
                    </Form.Group>

                    <div className="d-grid gap-2">
                      <Button
                        variant="primary"
                        type="submit"
                        size="lg"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Generating AI Response...
                          </>
                        ) : (
                          'Get AI Legal Advice'
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>

              {/* Response Display */}
              {showResponse && currentResponse && (
                <Card id="response-section" className="mb-4" style={{
                  animation: 'fadeIn 0.5s ease-in',
                  border: '1px solid rgba(201, 169, 97, 0.3)'
                }}>
                  <Card.Header style={{ background: 'rgba(201, 169, 97, 0.1)' }}>
                    <h5 style={{ margin: 0, color: '#c9a961' }}>
                      ✓ AI Legal Response Generated
                    </h5>
                    <small style={{ color: '#8b7355' }}>
                      {currentResponse.lawyerType} • {currentResponse.timestamp.toLocaleString()}
                    </small>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h6 style={{ color: '#c9a961', marginBottom: '0.75rem' }}>Your Question:</h6>
                      <p style={{ 
                        color: '#d4d4d4', 
                        padding: '1rem',
                        background: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: '6px',
                        borderLeft: '3px solid #c9a961',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {currentResponse.query}
                      </p>
                    </div>

                    <div>
                      <h6 style={{ color: '#c9a961', marginBottom: '0.75rem' }}>AI Legal Advice:</h6>
                      <div style={{ 
                        color: '#f5f5f0', 
                        padding: '1.5rem',
                        background: 'rgba(201, 169, 97, 0.05)',
                        borderRadius: '6px',
                        borderLeft: '3px solid #c9a961',
                        lineHeight: '1.8',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {currentResponse.response}
                      </div>
                    </div>

                    <div className="mt-4 d-flex gap-3">
                      <Button 
                        variant="primary" 
                        onClick={handleAskAnother}
                      >
                        Ask Another Question
                      </Button>
                      <Button 
                        variant="secondary" 
                        onClick={() => window.location.href = '/my-queries'}
                      >
                        View All My Queries
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Example Questions */}
              <Card className="mt-4">
                <Card.Header>
                  <h5 style={{ margin: 0 }}>Example Questions</h5>
                </Card.Header>
                <Card.Body>
                  <ul style={{ marginBottom: 0 }}>
                    <li>How can I file my taxes in Pakistan?</li>
                    <li>What are my rights under the Constitution regarding freedom of speech?</li>
                    <li>What is the punishment for theft under PPC?</li>
                    <li>How do I register a marriage in Pakistan?</li>
                    <li>What are the property inheritance laws in Pakistan?</li>
                  </ul>
                </Card.Body>
              </Card>
            </div>
          </div>
        </Container>

        <Contact />
      </main>
      <Footer />
    </div>
  );
};

export default AskLawyer;
